import type { GeoPoint, Locality, MagicPlaceStatus, PointOfInterest, Stage, TreeSummary } from '@api/types'

// Conversión de los datos de la API a GeoJSON para las capas del mapa (funciones puras).

export type Feature<P> = {
	type: 'Feature'
	geometry: { type: 'Point'; coordinates: [number, number] } | { type: 'LineString'; coordinates: [number, number][] }
	properties: P
}
export type FeatureCollection<P> = { type: 'FeatureCollection'; features: Feature<P>[] }
export type Bounds = [[number, number], [number, number]] // [[oeste, sur], [este, norte]]

// `stageId` sirve para quedarse solo con lo de una etapa al elegirla ('' si no tiene).
export type TreeProperties = { code: string; condition: string; speciesName: string; stageId: string }
// Los lugares mágicos van en la misma capa que los de interés; `magicId` los distingue.
export type PoiProperties = { id: string; category: string; name: string; stageId: string; magicId?: string }
export type StageProperties = { id: string; name: string }
export type StageEndProperties = { end: 'start' | 'finish' }
export type VariantProperties = { id: string; name: string }

const collection = <P>(features: Feature<P>[]): FeatureCollection<P> => ({ type: 'FeatureCollection', features })

// GeoJSON usa [longitud, latitud].
export const treesToGeoJson = (trees: TreeSummary[]): FeatureCollection<TreeProperties> =>
	collection(
		trees.map((tree) => ({
			type: 'Feature',
			geometry: { type: 'Point', coordinates: [tree.location.longitude, tree.location.latitude] },
			properties: {
				code: tree.code,
				condition: tree.latestCondition?.condition ?? 'none',
				speciesName: tree.speciesName ?? '',
				stageId: tree.stageId,
			},
		}))
	)

export const poisToGeoJson = (pois: PointOfInterest[]): FeatureCollection<PoiProperties> =>
	collection(
		pois.map((poi) => ({
			type: 'Feature',
			geometry: { type: 'Point', coordinates: [poi.location.longitude, poi.location.latitude] },
			properties: { id: poi.id, category: poi.category, name: poi.name, stageId: poi.stageId ?? '' },
		}))
	)

// Lugares mágicos en la capa de lugares: los aprobados con el icono de "lugar mágico"; los
// recibidos (solo los ve el personal), con el de "pendiente de revisar".
export const magicPlacesToGeoJson = (
	places: { id: string; name: string; location: GeoPoint; status: MagicPlaceStatus }[]
): FeatureCollection<PoiProperties> =>
	collection(
		places.map((place) => ({
			type: 'Feature',
			geometry: { type: 'Point', coordinates: [place.location.longitude, place.location.latitude] },
			properties: {
				id: `magic:${place.id}`,
				magicId: place.id,
				category: place.status === 'approved' ? 'magic_place' : 'magic_pending',
				name: place.name,
				stageId: '',
			},
		}))
	)

// Las variantes (pueblos del Camino fuera del recorrido principal); el resto de localidades no se
// pintan: ya están en el trazado.
export const variantsToGeoJson = (localities: Locality[]): FeatureCollection<VariantProperties> =>
	collection(
		localities
			.filter((locality) => locality.variant)
			.map((locality) => ({
				type: 'Feature',
				geometry: { type: 'Point', coordinates: [locality.location.longitude, locality.location.latitude] },
				properties: { id: locality.id, name: locality.name },
			}))
	)

export const stagesToGeoJson = (stages: Stage[]): FeatureCollection<StageProperties> =>
	collection(
		stages.map((stage) => ({
			type: 'Feature',
			geometry: { type: 'LineString', coordinates: stage.track.coordinates },
			properties: { id: stage.id, name: stage.name },
		}))
	)

// Salida y llegada de la etapa elegida (primer y último punto de su trazado); vacía si no hay
// etapa o su trazado no tiene puntos.
export const stageEndsToGeoJson = (stage: Stage | null): FeatureCollection<StageEndProperties> => {
	const coordinates = stage?.track.coordinates ?? []
	const start = coordinates[0]
	const finish = coordinates.at(-1)
	if (!start || !finish) return collection([])
	return collection([
		{ type: 'Feature', geometry: { type: 'Point', coordinates: start }, properties: { end: 'start' } },
		{ type: 'Feature', geometry: { type: 'Point', coordinates: finish }, properties: { end: 'finish' } },
	])
}

// Rectángulo que contiene todas las geometrías; null si no hay ninguna coordenada.
export const boundsOf = (collections: FeatureCollection<unknown>[]): Bounds | null => {
	let west = Number.POSITIVE_INFINITY
	let south = Number.POSITIVE_INFINITY
	let east = Number.NEGATIVE_INFINITY
	let north = Number.NEGATIVE_INFINITY
	for (const { features } of collections)
		for (const { geometry } of features) {
			const points = geometry.type === 'Point' ? [geometry.coordinates] : geometry.coordinates
			for (const [lng, lat] of points) {
				west = Math.min(west, lng)
				east = Math.max(east, lng)
				south = Math.min(south, lat)
				north = Math.max(north, lat)
			}
		}
	return Number.isFinite(west)
		? [
				[west, south],
				[east, north],
			]
		: null
}
